// API Configuration for Admin Portal
// This matches the old admin panel configuration pattern
// For production, set REACT_APP_API_BASE_URL environment variable
// Example: REACT_APP_API_BASE_URL=https://api.formbuilder.ai
// Note: The '/api' part is appended in the endpoint calls, just like the old admin

import axios from 'axios';

// Safe env access for browser (process may be undefined without CRA injection)
const getEnv = () => {
  try {
    return typeof process !== 'undefined' && process && process.env
      ? process.env
      : {};
  } catch (e) {
    return {};
  }
};

// Detect if we're in production based on hostname
const isProduction =
  typeof window !== 'undefined' &&
  window.location &&
  window.location.hostname !== 'localhost' &&
  window.location.hostname !== '127.0.0.1' &&
  !window.location.hostname.startsWith('192.168.') &&
  !window.location.hostname.startsWith('10.') &&
  !window.location.hostname.startsWith('172.');

const getApiBaseUrl = () => {
  const env = getEnv();
  if (env.REACT_APP_API_BASE_URL) {
    return env.REACT_APP_API_BASE_URL;
  }
  if (typeof window !== 'undefined' && window.location && isProduction) {
    const hostname = window.location.hostname;
    if (hostname.includes('formbuilder.ai')) {
      return 'https://formbuilder.ai';
    }
    return `${window.location.protocol}//${hostname}`;
  }
  return 'http://localhost:8002';
};

const getFormViewBaseUrl = () => {
  const env = getEnv();
  if (env.REACT_APP_FORM_VIEW_BASE_URL) {
    return env.REACT_APP_FORM_VIEW_BASE_URL;
  }
  if (typeof window !== 'undefined' && window.location && isProduction) {
    const hostname = window.location.hostname;
    if (hostname.includes('formbuilder.ai')) {
      return 'https://app.formbuilder.ai';
    }
    return `${window.location.protocol}//${hostname}`;
  }
  return 'http://localhost:3007';
};

export const API_BASE_URL = getApiBaseUrl();
export const FORM_VIEW_BASE_URL = getFormViewBaseUrl();

/**
 * Build the full public URL to view/fill a form.
 * Form "link" from API is a slug (e.g. "my-form-abc"); the app serves forms at /en/form/{slug}.
 * @param {{ link?: string, _id?: string, id?: string }} form - form object with link or _id
 * @returns {string} full URL to open the form in a new tab
 */
export function getFormViewUrl(form) {
  if (!form) return '#';
  const slug = form.link || form._id || form.id;
  if (!slug) return '#';
  if (typeof slug === 'string' && slug.startsWith('http')) return slug;
  const base = FORM_VIEW_BASE_URL.replace(/\/$/, '');
  return `${base}/en/form/${slug}`;
}
export const ADMIN_LOGIN_ENDPOINT = '/form-admin/sendlogin';
export const ADMIN_VERIFY_OTP_ENDPOINT = '/form-admin/verifyLoginOtp';
export const ADMIN_RESEND_OTP_ENDPOINT = '/form-admin/resendLoginOtp';
export const ADMIN_LOGOUT_ENDPOINT = '/form-admin/logout';
export const ADMIN_INFO_ENDPOINT = '/form-admin/getAdminInfo';
export const GET_WORKSPACES_FOR_ADMIN_FILTER = '/api/getWorkspacesForAdminFilter';
export const GET_ADMIN_DASHBOARD_WORKSPACE_STATS = '/api/getAdminDashboardWorkspaceStats';

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem('admin_token');
};

// Set auth token in localStorage
export const setAuthToken = (token) => {
  localStorage.setItem('admin_token', token);
};

// Remove auth token from localStorage
export const removeAuthToken = () => {
  localStorage.removeItem('admin_token');
};

// Get axios config with auth token
export const getAxiosConfig = () => {
  const token = getAuthToken();
  return {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    withCredentials: true,
  };
};

// Global 401 interceptor on the default axios instance.
// All components use `axios` directly (not axiosInstance), so this
// catches expired/invalid tokens everywhere and redirects to login.
let isRedirecting = false;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401 && !isRedirecting) {
      const url = error.config?.url || '';

      // Ignore user-only endpoints that are expected to 401 in admin app
      const userEndpoints = ['/api/getMe', '/api/GetMyGeneralDetails', '/api/getTrashedForms'];
      const isUserEndpoint = userEndpoints.some((ep) => url.includes(ep));

      // Ignore auth endpoints (login/OTP) — 401 there means bad credentials, not expired session
      const isAuthEndpoint = url.includes('/form-admin/sendlogin') ||
        url.includes('/form-admin/verifyLoginOtp') ||
        url.includes('/form-admin/resendLoginOtp');

      if (!isUserEndpoint && !isAuthEndpoint) {
        isRedirecting = true;
        removeAuthToken();
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
        // Reset flag after a short delay to allow the redirect to complete
        setTimeout(() => { isRedirecting = false; }, 2000);
      }
    }
    return Promise.reject(error);
  },
);

// Create a configured axios instance (legacy — kept for backward compatibility)
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
});

/**
 * Extract a user-friendly error message from an API error response.
 * Prefers validation errors (e.g. "Password must be between 8 and 36 characters.")
 * over the generic "The given data was invalid." message.
 */
export function getApiErrorMessage(err, fallback = 'An error occurred') {
  const data = err?.response?.data;
  if (!data) return err?.message || fallback;
  if (data.errors && typeof data.errors === 'object') {
    const parts = [];
    Object.keys(data.errors).forEach((key) => {
      const val = data.errors[key];
      const msg = Array.isArray(val) ? val.join(' ') : String(val || '');
      if (msg) parts.push(msg);
    });
    if (parts.length) return parts.join(' ');
  }
  return data.error || data.message || fallback;
}

export { axiosInstance };
